import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { 
    Typography, 
    Card, 
    Modal, 
    Box, 
    Button, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel,
    Chip,
    Alert,
    Grid
} from '@mui/material';
import { FONT_FAMILY, MAIN_COLOR, SECONDORY_COLOR } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { api } from 'common';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export default function FleetBookingManagement() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const dispatch = useDispatch();
    const { updateBooking } = api;
    
    const auth = useSelector(state => state.auth);
    const bookingdata = useSelector(state => state.bookinglistdata);
    const usersdata = useSelector(state => state.usersdata);
    const settings = useSelector(state => state.settingsdata.settings);
    
    const [data, setData] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [selectedDriver, setSelectedDriver] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const loaded = useRef(false);

    function formatAmount(value, decimal, country) {
        const number = parseFloat(value || 0);
        if (country === "Vietnam") {
            return number.toLocaleString("vi-VN", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        } else {
            return number.toLocaleString("en-US", {
                minimumFractionDigits: decimal,
                maximumFractionDigits: decimal
            });
        }
    }

    // Filtrer les réservations pour le FleetAdmin
    useEffect(() => {
        if (bookingdata.bookings && auth.profile && auth.profile.uid) {
            const fleetBookings = bookingdata.bookings.filter(booking => {
                // Réservations où le chauffeur appartient au FleetAdmin OU pas encore affectées
                const isFleetDriver = booking.driver_info && 
                                     booking.driver_info.fleetadmin === auth.profile.uid;
                const isUnassigned = booking.status === 'NEW' && 
                                    booking.fleetadmin === auth.profile.uid;
                
                // Statuts qui nous intéressent : NEW, ACCEPTED, ARRIVED, STARTED
                const relevantStatus = ['NEW', 'ACCEPTED', 'ARRIVED', 'STARTED'].includes(booking.status);
                
                return relevantStatus && (isFleetDriver || isUnassigned);
            });
            
            setData(fleetBookings);
        } else {
            setData([]);
        }
        loaded.current = true;
    }, [bookingdata.bookings, auth.profile]);

    // Récupérer les chauffeurs actifs de la flotte
    useEffect(() => {
        if (usersdata.users && auth.profile && auth.profile.uid) {
            const activeDrivers = usersdata.users.filter(user => 
                user.usertype === 'driver' &&
                user.fleetadmin === auth.profile.uid &&
                user.approved === true &&
                user.driverActiveStatus === true &&
                !user.queue // Pas en file d'attente (disponible)
            );
            setDrivers(activeDrivers);
        }
    }, [usersdata.users, auth.profile]);

    const handleOpenModal = (booking) => {
        setSelectedBooking(booking);
        setSelectedDriver('');
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedBooking(null);
        setSelectedDriver('');
    };

    const handleAssignDriver = () => {
        if (!selectedDriver || !selectedBooking) return;

        const driver = drivers.find(d => d.id === selectedDriver);
        if (!driver) return;

        const updateData = {
            driver: driver.id,
            driver_info: {
                uid: driver.id,
                firstName: driver.firstName,
                lastName: driver.lastName,
                mobile: driver.mobile,
                email: driver.email,
                profile_image: driver.profile_image || null,
                carType: driver.carType,
                carImage: driver.carImage || null,
                vehicleNumber: driver.vehicleNumber || '',
                vehicleModel: driver.vehicleModel || '',
                fleetadmin: driver.fleetadmin || null,
                location: driver.location || null
            },
            driver_name: `${driver.firstName} ${driver.lastName}`,
            driverDeviceToken: driver.pushToken || '',
            status: 'ACCEPTED',
            requestedDrivers: {
                [driver.id]: {
                    driverRequestedAt: new Date().getTime(),
                    fleetAssigned: true, // Marquer comme affecté par le FleetAdmin
                    assignedBy: auth.profile.uid
                }
            }
        };

        dispatch(updateBooking(selectedBooking.id, updateData));
        handleCloseModal();
    };

    const handleAutoAssign = () => {
        if (!selectedBooking) return;

        // Laisser le système automatique prendre le relais
        const updateData = {
            status: 'NEW',
            autoDispatch: true, // Forcer la répartition automatique
            fleetManualOverride: false
        };

        dispatch(updateBooking(selectedBooking.id, updateData));
        handleCloseModal();
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'NEW': return colors.YELLOW;
            case 'ACCEPTED': return colors.BLUE;
            case 'ARRIVED': return colors.PURPLE;
            case 'STARTED': return colors.GREEN;
            default: return colors.BORDER;
        }
    };

    const getBookingType = (booking) => {
        if (booking.booking_type_admin) return t('booking_type_admin');
        if (booking.tripdate && new Date(booking.tripdate) > new Date()) {
            return t('booking_type_later');
        }
        return t('booking_type_now');
    };

    const columns = [
        { 
            title: t('booking_ref'), 
            field: 'reference',
            render: rowData => (
                <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
                    {rowData.reference || rowData.id?.substring(0, 8)}
                </Typography>
            )
        },
        {
            title: t('booking_date'),
            field: 'tripdate',
            render: rowData => moment(rowData.tripdate).format('DD/MM/YYYY HH:mm'),
            defaultSort: 'asc'
        },
        {
            title: t('booking_type'),
            field: 'bookingType',
            render: rowData => (
                <Chip 
                    icon={<ScheduleIcon />}
                    label={getBookingType(rowData)}
                    size="small"
                    style={{ fontFamily: FONT_FAMILY }}
                />
            )
        },
        {
            title: t('customer_name'),
            field: 'customer_name',
            render: rowData => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <PersonIcon fontSize="small" />
                    <Typography style={{ fontFamily: FONT_FAMILY }}>
                        {rowData.customer_name}
                    </Typography>
                </div>
            )
        },
        {
            title: t('pickup_address'),
            field: 'pickupAddress',
            render: rowData => (
                <Typography 
                    style={{ 
                        fontFamily: FONT_FAMILY, 
                        fontSize: 12,
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {rowData.pickupAddress}
                </Typography>
            )
        },
        {
            title: t('drop_address'),
            field: 'dropAddress',
            render: rowData => (
                <Typography 
                    style={{ 
                        fontFamily: FONT_FAMILY, 
                        fontSize: 12,
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {rowData.dropAddress}
                </Typography>
            )
        },
        {
            title: t('car_type'),
            field: 'carType',
            render: rowData => (
                <Chip 
                    icon={<DirectionsCarIcon />}
                    label={rowData.carType}
                    size="small"
                    color="primary"
                    style={{ fontFamily: FONT_FAMILY }}
                />
            )
        },
        {
            title: t('estimate_fare'),
            field: 'estimate',
            render: rowData => (
                <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: MAIN_COLOR }}>
                    {rowData.estimate ? 
                        settings.swipe_symbol
                            ? formatAmount(rowData.estimate, settings.decimal, settings.country) + " " + settings.symbol
                            : settings.symbol + " " + formatAmount(rowData.estimate, settings.decimal, settings.country)
                        : '-'
                    }
                </Typography>
            )
        },
        {
            title: t('driver'),
            field: 'driver_name',
            render: rowData => rowData.driver_name ? (
                <Chip 
                    icon={<AssignmentIndIcon />}
                    label={rowData.driver_name}
                    size="small"
                    color="success"
                    style={{ fontFamily: FONT_FAMILY }}
                />
            ) : (
                <Chip 
                    label={t('driver_not_assigned')}
                    size="small"
                    color="warning"
                    style={{ fontFamily: FONT_FAMILY }}
                />
            )
        },
        {
            title: t('status'),
            field: 'status',
            render: rowData => (
                <Chip 
                    label={t(rowData.status)}
                    size="small"
                    style={{ 
                        backgroundColor: getStatusColor(rowData.status),
                        color: colors.WHITE,
                        fontFamily: FONT_FAMILY,
                        fontWeight: 'bold'
                    }}
                />
            )
        }
    ];

    return !loaded.current ? (
        <CircularLoading />
    ) : (
        <div style={{ padding: 20 }}>
            <Card
                style={{
                    borderRadius: "19px",
                    backgroundColor: colors.WHITE,
                    minHeight: 100,
                    padding: 20,
                    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
                }}
            >
                <Grid container spacing={2} style={{ marginBottom: 20 }}>
                    <Grid item xs={12}>
                        <Typography 
                            variant="h5" 
                            style={{ 
                                fontFamily: FONT_FAMILY, 
                                fontWeight: 'bold',
                                color: MAIN_COLOR,
                                textAlign: isRTL === 'rtl' ? 'right' : 'left'
                            }}
                        >
                            {t('fleet_booking_management')}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            style={{ 
                                fontFamily: FONT_FAMILY,
                                marginTop: 10,
                                textAlign: isRTL === 'rtl' ? 'right' : 'left'
                            }}
                        >
                            {t('fleet_booking_description')}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card style={{ padding: 15, backgroundColor: colors.YELLOW + '20', textAlign: 'center' }}>
                            <Typography variant="h4" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: colors.YELLOW }}>
                                {data.filter(b => b.status === 'NEW').length}
                            </Typography>
                            <Typography variant="body2" style={{ fontFamily: FONT_FAMILY }}>
                                {t('new_bookings')}
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card style={{ padding: 15, backgroundColor: colors.BLUE + '20', textAlign: 'center' }}>
                            <Typography variant="h4" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: colors.BLUE }}>
                                {data.filter(b => b.status === 'ACCEPTED').length}
                            </Typography>
                            <Typography variant="body2" style={{ fontFamily: FONT_FAMILY }}>
                                {t('accepted_bookings')}
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card style={{ padding: 15, backgroundColor: colors.GREEN + '20', textAlign: 'center' }}>
                            <Typography variant="h4" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: colors.GREEN }}>
                                {drivers.length}
                            </Typography>
                            <Typography variant="body2" style={{ fontFamily: FONT_FAMILY }}>
                                {t('active_drivers')}
                            </Typography>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card style={{ padding: 15, backgroundColor: colors.PURPLE + '20', textAlign: 'center' }}>
                            <Typography variant="h4" style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold', color: colors.PURPLE }}>
                                {data.filter(b => b.status === 'STARTED').length}
                            </Typography>
                            <Typography variant="body2" style={{ fontFamily: FONT_FAMILY }}>
                                {t('ongoing_trips')}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                <ThemeProvider theme={theme}>
                    <MaterialTable
                        title={t('fleet_bookings_list')}
                        columns={columns}
                        data={data}
                        onRowClick={(evt, selectedRow) =>
                            setSelectedRow(selectedRow.tableData.id)
                        }
                        options={{
                            exportButton: true,
                            rowStyle: rowData => ({
                                backgroundColor: (selectedRow === rowData.tableData.id) ? colors.THIRDCOLOR : colors.WHITE
                            }),
                            ...TableStyle()
                        }}
                        localization={localization(t)}
                        actions={[
                            rowData => ({
                                icon: () => <AssignmentIndIcon style={{ color: MAIN_COLOR }} />,
                                tooltip: t('fleet_assign_driver'),
                                onClick: (event, rowData) => handleOpenModal(rowData),
                                disabled: rowData.status !== 'NEW'
                            })
                        ]}
                        style={{
                            direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
                            fontFamily: FONT_FAMILY
                        }}
                    />
                </ThemeProvider>
            </Card>

            {/* Modal d'affectation de chauffeur */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="assign-driver-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: 600 },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    p: 4,
                }}>
                    <Typography 
                        variant="h6" 
                        component="h2" 
                        style={{ 
                            fontFamily: FONT_FAMILY, 
                            fontWeight: 'bold',
                            marginBottom: 20,
                            color: MAIN_COLOR
                        }}
                    >
                        {t('assign_driver_to_booking')}
                    </Typography>

                    {selectedBooking && (
                        <Alert severity="info" style={{ marginBottom: 20 }}>
                            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                                <strong>{t('booking_ref')}:</strong> {selectedBooking.reference || selectedBooking.id?.substring(0, 8)}
                            </Typography>
                            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                                <strong>{t('customer')}:</strong> {selectedBooking.customer_name}
                            </Typography>
                            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                                <strong>{t('pickup')}:</strong> {selectedBooking.pickupAddress}
                            </Typography>
                            <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                                <strong>{t('car_type')}:</strong> {selectedBooking.carType}
                            </Typography>
                        </Alert>
                    )}

                    {drivers.length > 0 ? (
                        <>
                            <FormControl fullWidth style={{ marginBottom: 20 }}>
                                <InputLabel id="driver-select-label" style={{ fontFamily: FONT_FAMILY }}>
                                    {t('fleet_select_driver')}
                                </InputLabel>
                                <Select
                                    labelId="driver-select-label"
                                    id="driver-select"
                                    value={selectedDriver}
                                    label={t('fleet_select_driver')}
                                    onChange={(e) => setSelectedDriver(e.target.value)}
                                    style={{ fontFamily: FONT_FAMILY }}
                                >
                                    {drivers.map((driver) => (
                                        <MenuItem 
                                            key={driver.id} 
                                            value={driver.id}
                                            style={{ fontFamily: FONT_FAMILY }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                                                <DirectionsCarIcon />
                                                <div style={{ flex: 1 }}>
                                                    <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
                                                        {driver.firstName} {driver.lastName}
                                                    </Typography>
                                                    <Typography style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: colors.BORDER }}>
                                                        {driver.carType} - {driver.vehicleNumber || t('no_vehicle')}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={handleAssignDriver}
                                    disabled={!selectedDriver}
                                    style={{ 
                                        fontFamily: FONT_FAMILY,
                                        flex: 1,
                                        backgroundColor: MAIN_COLOR
                                    }}
                                >
                                    {t('assign_manually')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleAutoAssign}
                                    style={{ 
                                        fontFamily: FONT_FAMILY,
                                        flex: 1
                                    }}
                                >
                                    {t('fleet_auto_dispatch')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Alert severity="warning">
                            <Typography style={{ fontFamily: FONT_FAMILY }}>
                                {t('no_active_drivers')}
                            </Typography>
                        </Alert>
                    )}

                    <Button
                        variant="text"
                        startIcon={<CancelIcon />}
                        onClick={handleCloseModal}
                        style={{ 
                            fontFamily: FONT_FAMILY,
                            marginTop: 15,
                            width: '100%'
                        }}
                    >
                        {t('cancel')}
                    </Button>
                </Box>
            </Modal>
        </div>
    );
}
